/**
 * №22.3.1 (D-AF1/D-AF4) + №22.3.2 (F1) — the App-Shell scope, resolved to a NAMED
 * anchor + actor, ONCE, for every consumer.
 *
 * "Here" means one thing everywhere (D-CH1): the work rail's conversation and the
 * docked panel (`CoPanel`) both read this hook, so the chip they show is the SAME
 * string — the resolved anchor node's name, never a route literal (the ruled D-CV9
 * regression). №22.3.2 closed the last gap in that claim: the bar used to render the
 * ROUTE's chip while the panel rendered the SERVER's, so `/app` read "Needs you" in
 * one place and "Everywhere" in the other. The server-resolved name is now folded in
 * here, above both consumers.
 *
 * Where the chip comes from is declared per surface by the matrix (`chipSource`):
 *  - `"node"` — an agent / record / place / task anchor: the SERVER's resolved name
 *    wins (D-AF1). The matrix row's label is only the first-paint placeholder.
 *  - `"surface"` — no node exists in the anchor vocabulary (the ledger pages, the WIP
 *    worksheet, firm settings, the Needs-You home): the row's own label stands, and it
 *    names the context the conversation is actually given.
 *
 * It also resolves the default ACTOR (D-AF4): a hire's desk → that hire; every other
 * anchor → Kuvio, the un-personified workspace chat (never a hire uninvited).
 *
 * The anchor-stream read here shares react-query's cache entry with the panel's own
 * subscription (same key), so naming the anchor costs no extra request — the panel
 * still owns the LIVE subscription (D-CV11: nothing double-subscribes).
 */
import { useMemo } from "react";
import { useLocation } from "react-router";
import { useLibraryNode, useWorkspaceInfo } from "../../lib/hooks/use-kuvio";
import { useAnchorStream } from "./use-anchor-stream";
import { defaultActorForAnchor, type RouteContext } from "../routes/app/app-scope";
import { resolveSurface, type RouteAnchor, type SubjectKind } from "../routes/app/route-anchor-matrix";
import type { StreamActor } from "../../lib/types";

export interface AppScope {
  ctx: RouteContext;
  anchor: RouteAnchor;
  /** The chip text — one string for the bar AND the panel (D-AF1 / F1). */
  chip: string;
  /** The default actor for this anchor (D-AF4) — named before you speak. */
  actor: StreamActor;
  /** The matrix row that resolved this surface — the CI journey asserts against it. */
  surfaceKey: string;
  /** №29 (S2) — what the centre stage is holding. Read off the SAME row as the
   *  anchor, so a consumer that behaves differently on a chat subject (the
   *  conversation, which stops naming the anchor when the stage head names it —
   *  Q3) cannot disagree with the shell about which subject is on stage. */
  subject: SubjectKind;
}

export function useAppScope(): AppScope {
  const location = useLocation();
  const wsInfo = useWorkspaceInfo();

  const surface = useMemo(() => resolveSurface(location.pathname), [location.pathname]);
  const anchor = surface.anchor;
  const ctx = useMemo<RouteContext>(
    () => ({ chip: surface.chip, scope: surface.scope, page: surface.page, placeholder: surface.placeholder }),
    [surface],
  );

  // A staff desk anchors on the hire node — resolve its name from cache so the chip is
  // "@{HireName}" even before the stream answers (nested team agents included).
  const hireId = anchor.kind === "agent" ? anchor.id : undefined;
  const hireNode = useLibraryNode(hireId);
  const rootName = (wsInfo.data?.library_root ?? []).find((n) => n.id === hireId)?.name;
  const hireName = hireNode.data?.name ?? rootName ?? null;

  // The authoritative name for a NODE anchor. Shared cache entry with the panel's live
  // subscription — this observer never drives the poll (`live: false`).
  const identity = useAnchorStream(anchor, { live: false });
  const serverName = identity.data?.anchor?.name ?? null;
  const serverActor = identity.data?.actor ?? null;

  const chip = surface.chipSource === "surface"
    ? surface.chip
    : (serverName ?? hireName ?? surface.chip);

  const actor = useMemo(
    () =>
      serverActor ??
      defaultActorForAnchor(anchor.kind, {
        hire: hireId && hireName ? { id: hireId, name: hireName } : null,
        rootId: wsInfo.data?.root_node_id ?? null,
      }),
    [serverActor, anchor.kind, hireId, hireName, wsInfo.data?.root_node_id],
  );

  return { ctx, anchor, chip, actor, surfaceKey: surface.key, subject: surface.subject };
}
